import { Template } from '../Template';
import { Handlebars } from '../../Utility/Handlebars/databaseHandlebars';
import { Vue } from '../../Utility/Vue';
import { $ } from '../../../vendor/jquery';
import { VueInjectedMethods, VueLifecycleHook, VuePropTypes } from './types';
import { deepClone } from '../../Utility/DeepClone';
import './vuetemplate.scss';

export abstract class VueTemplate<VueStoreData extends Record<string, unknown>> extends Template {
    /**
     * The property data type constructors that Vue uses to populate the template with data.
     * This schema needs to align with the VueContext generic.
     * @see {@link https://v2.vuejs.org/v2/guide/components-props.html#Prop-Types}
     */
    abstract properties: {
        [key in keyof VueStoreData]: VuePropTypes;
    };

    /**
     * Private dataStore
     */
    #dataStoreData: VueStoreData = <VueStoreData>{};

    /**
     * Privately holds the Vue instance, use getter/setter
     * Can only be set once, enforced by setter
     */
    #vueInstance: Vue | null = null;

    get name(): string {
        return `vue-template-${this.wrapperElement.id}`;
    }

    protected get dataStore(): VueStoreData {
        return this.#dataStoreData;
    }

    protected set dataStore(value: VueStoreData) {
        if (Object.prototype.toString.call(value) === '[object Object]') {
            this.#dataStoreData = value;
        } else {
            throw new TypeError('Value should be an Object');
        }
    }

    protected get vueInstance(): Vue | null {
        return this.#vueInstance;
    }

    protected set vueInstance(newVue: Vue | null) {
        if (newVue instanceof Vue && this.#vueInstance === null) {
            this.#vueInstance = newVue;
        }
    }

    /**
     * Insert HTML to wrapper node
     */
    async render(context: VueStoreData): Promise<void> {
        //If vue instance is already created then update the dataStore and short circuit
        if (this.vueInstance) {
            this.updateDataStore(context);
            return;
        }

        //create standard data object that will be used by the template
        this.createDataStore(context);

        // Add a div to the DOM. It will be replaced by the Vue Template
        // Component. no longer use append as it was causing the duplication
        // of view templates to be added into the widget
        const vueWrapperId = `vue_wrapper_${this.wrapperElement.id}`;
        $(this.wrapperElement).html(
            `<div id="${vueWrapperId}"></div><div style="display:none" id="schedule-player"></div>`
        );

        //css is added to the DOM
        // todo: can we create a scoped css style for the vue template?
        //Extract the widget id from the wrapper element
        //todo: This method of obtaining the widgetId sucks!  Can it be done in a less janky way?
        //  It could be possible to grab it from context, but that couples vue templates with widget
        //  which we were trying to avoid
        const widgetId = this.wrapperElement.id.replace('widget_', '');
        this.addCssToDOM(`vue_css_${widgetId}`, this.css, context);

        this.registerTemplate();

        this.vueInstance = new Vue({
            el: `#${vueWrapperId}`,
            template: `<div class="vue-template">
                    <component v-bind:is="templateName" v-bind="dataStore"></component>
                </div>`,
            data: {
                templateName: this.name,
                dataStore: this.dataStore,
            },
            methods: {},
        });

        // wait for the DOM to finish rendering
        return Vue.nextTick();
    }

    /**
     * Creates child Vue component
     * @todo add as local component rather than global
     */
    private registerTemplate(): void {
        const jsMethods = this.generateMethods();
        const vueComponentContext = Object.assign(
            {
                props: this.properties,
                template: this.html,
                methods: jsMethods.methods,
            },
            jsMethods.lifeCycleHooks
        );

        /**
         * NOTE - For whatever reason, Vue is not happy without the 'as' assertion
         * Unfortunately, the recommended use or PropType does not seem to exist
         * @see {@link https://v2.vuejs.org/v2/guide/typescript.html#Annotating-Props}
         */
        Vue.component(this.name, vueComponentContext as Record<string, unknown>);
    }

    /**
     * Assigns data to dataStore
     */
    protected createDataStore(context: VueStoreData): void {
        this.dataStore = deepClone(context);
    }

    /**
     * Updates data store. Can be overridden to update dataStore appropriately
     */
    protected updateDataStore(context: VueStoreData): void {
        const clone = deepClone(context);

        //Clear out dataStore, but preserve reference
        Object.keys(this.dataStore).forEach((key) => delete this.dataStore[key]);

        //Shallow copy deepClone to dataStore
        Object.assign(this.dataStore, clone);
    }

    /**
     * Convert js string to functions
     */
    private generateMethods(): VueInjectedMethods {
        const lifeCycleEvents = new Set<VueLifecycleHook>([
            VueLifecycleHook.BEFORE_CREATE,
            VueLifecycleHook.CREATED,
            VueLifecycleHook.BEFORE_MOUNT,
            VueLifecycleHook.MOUNTED,
            VueLifecycleHook.BEFORE_UPDATE,
            VueLifecycleHook.UPDATED,
            VueLifecycleHook.BEFORE_DESTROY,
            VueLifecycleHook.DESTROYED,
        ]);
        const jsObject = new Function(`return ${this.js}`)();
        const jsMethods: VueInjectedMethods = {
            methods: {},
            lifeCycleHooks: {},
        };

        for (const methodName in jsObject) {
            //check if it is component method or component lifecycle method
            if (!lifeCycleEvents.has(methodName as VueLifecycleHook)) {
                //Not a lifecycle method
                jsMethods.methods[methodName] = new Function(`return ${jsObject[methodName]}`)();
            } else {
                jsMethods.lifeCycleHooks[methodName as VueLifecycleHook] = new Function(
                    `return ${jsObject[methodName]}`
                )();
            }
        }

        return jsMethods;
    }

    /**
     * Inserts CSS into the DOM
     */
    private addCssToDOM(id: string, css: string | null, context: VueStoreData): void {
        const style = document.createElement('style');
        style.type = 'text/css';
        style.id = id;
        if (context) {
            //todo: Remove reliance on Handlebars
            style.appendChild(document.createTextNode(Handlebars.compile(css)(context)));
        } else {
            style.appendChild(document.createTextNode(css || ''));
        }

        $(this.wrapperElement).prepend(style);
    }
}
